import React, { useRef, useState } from 'react'

export interface TabItem {
    key: string
    label: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    disabled?: boolean
}

export interface TabsProps {
    items: TabItem[]
    variant?: 'primary' | 'secondary'
    behavior?: 'hug' | 'fill'
    /**
     * Divergence from @numeo-ai/components: `lg` sets the labels in 16px for pages where the tabs are the main navigation; `mdlg` is 15px with
     * roomier padding, for a tab strip that sits beside denser content and reads small at 14px.
     */
    size?: 'md' | 'mdlg' | 'lg'
    /** Divergence from @numeo-ai/components: the selected underline tab reads in the accent colour (blue), as the statement pages' tabs do. */
    accentActive?: boolean
    selectedKey?: string
    defaultSelectedKey?: string
    onSelectionChange?: (key: string) => void
    style?: React.CSSProperties
    /**
     * Gives each tab the id `${idPrefix}-tab-${key}` and `aria-controls` `${idPrefix}-panel-${key}`, so a panel that carries that id and
     * `aria-labelledby` the tab's id is tied to it for assistive technology.
     */
    idPrefix?: string
}

export const getTabId = (idPrefix: string, key: string) => `${idPrefix}-tab-${key}`
export const getTabPanelId = (idPrefix: string, key: string) => `${idPrefix}-panel-${key}`

export const Tabs = ({
    items,
    variant = 'primary',
    behavior = 'hug',
    size = 'md',
    accentActive = false,
    selectedKey: controlledKey,
    defaultSelectedKey,
    onSelectionChange,
    style,
    idPrefix,
}: TabsProps) => {
    const [internalKey, setInternalKey] = useState(defaultSelectedKey ?? items[0]?.key)
    const activeKey = controlledKey !== undefined ? controlledKey : internalKey
    const isPrimary = variant === 'primary'
    const isFill = behavior === 'fill'
    const isLarge = size === 'lg'
    const isMidLarge = size === 'mdlg'

    const handleSelect = (key: string) => {
        if (controlledKey === undefined) setInternalKey(key)
        onSelectionChange?.(key)
    }

    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
    const enabledKeys = items.filter((item) => !item.disabled).map((item) => item.key)

    // Arrow keys, Home and End move between the enabled tabs (the active one is the only tab stop), as the tab pattern expects.
    const handleKeyDown = (event: React.KeyboardEvent, key: string) => {
        const index = enabledKeys.indexOf(key)
        if (index === -1) return
        const target =
            event.key === 'ArrowRight'
                ? enabledKeys[(index + 1) % enabledKeys.length]
                : event.key === 'ArrowLeft'
                  ? enabledKeys[(index - 1 + enabledKeys.length) % enabledKeys.length]
                  : event.key === 'Home'
                    ? enabledKeys[0]
                    : event.key === 'End'
                      ? enabledKeys[enabledKeys.length - 1]
                      : undefined
        if (!target) return
        event.preventDefault()
        handleSelect(target)
        tabRefs.current.get(target)?.focus()
    }

    const tabProps = (key: string, isActive: boolean) => ({
        ref: (node: HTMLButtonElement | null) => {
            if (node) tabRefs.current.set(key, node)
            else tabRefs.current.delete(key)
        },
        id: idPrefix ? getTabId(idPrefix, key) : undefined,
        'aria-controls': idPrefix ? getTabPanelId(idPrefix, key) : undefined,
        tabIndex: isActive ? 0 : -1,
        onKeyDown: (event: React.KeyboardEvent) => handleKeyDown(event, key),
    })

    if (isPrimary) {
        return (
            <div role="tablist" className="inline-flex items-center gap-0.5 overflow-hidden rounded-[28px] bg-default p-1" style={style}>
                {items.map((item) => {
                    const isActive = activeKey === item.key
                    return (
                        <button
                            key={item.key}
                            type="button"
                            role="tab"
                            {...tabProps(item.key, isActive)}
                            aria-selected={isActive}
                            disabled={item.disabled}
                            onClick={() => !item.disabled && handleSelect(item.key)}
                            className={`relative box-border flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[32px] border-none py-1.5 font-medium leading-[1.43] outline-none ${isLarge ? 'px-4 text-base' : isMidLarge ? 'px-4 text-[15px]' : 'px-3 text-sm'} transition-[background,color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-accent
                                disabled:cursor-not-allowed disabled:opacity-50
                                ${isActive ? 'bg-surface text-foreground shadow-tab' : 'bg-transparent text-foreground-muted enabled:hover:bg-background-inverse/5'}
                                ${isFill ? 'min-w-0 flex-1' : ''}`}
                        >
                            {item.prefix && <span className="flex shrink-0 items-center">{item.prefix}</span>}
                            {item.label}
                            {item.suffix && <span className="flex shrink-0 items-center">{item.suffix}</span>}
                        </button>
                    )
                })}
            </div>
        )
    }

    // Secondary variant — underline style
    return (
        <div role="tablist" className="inline-flex items-center border-b border-border" style={style}>
            {items.map((item) => {
                const isActive = activeKey === item.key
                return (
                    <button
                        key={item.key}
                        type="button"
                        role="tab"
                        {...tabProps(item.key, isActive)}
                        aria-selected={isActive}
                        disabled={item.disabled}
                        onClick={() => !item.disabled && handleSelect(item.key)}
                        className={`-mb-px box-border flex items-center justify-center gap-1.5 whitespace-nowrap border-x-0 border-b-2 border-t-0 border-solid bg-transparent font-medium leading-[1.43] outline-none ${isLarge ? 'px-4 pb-2 pt-1.5 text-base' : isMidLarge ? 'px-5 pb-2.5 pt-1.5 text-[15px]' : 'px-3 pb-1.5 pt-1 text-sm'} transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-accent
                            disabled:cursor-not-allowed disabled:opacity-50
                            ${isActive ? `border-accent ${accentActive ? 'text-accent' : 'text-foreground'}` : 'border-transparent text-foreground-muted enabled:hover:text-foreground'}
                            ${isFill ? 'min-w-0 flex-1' : ''}`}
                    >
                        {item.prefix && <span className="flex shrink-0 items-center">{item.prefix}</span>}
                        {item.label}
                        {item.suffix && <span className="flex shrink-0 items-center">{item.suffix}</span>}
                    </button>
                )
            })}
        </div>
    )
}
